import React, {useEffect, useMemo, useState} from "react";
import {Alert, Button, Card, Spinner, Table} from "react-bootstrap";
import HelpTooltip from "../HelpTooltip.jsx";
import SelectList from "../SelectList.jsx";
import useStatisticsData from "./useStatisticsData.js";
import {ABC_COLORS, formatDateDE, formatEuro, formatNumber, OTHER_GROUP_COLOR} from "./format.js";

const WINDOW_OPTIONS = [
    {value: "12", label: "Fenster: 12 Monate ohne/wenig Umsatz"},
    {value: "24", label: "Fenster: 24 Monate ohne/wenig Umsatz"},
];

const PER_PAGE_OPTIONS = [
    {value: "25", label: "25 pro Seite"},
    {value: "50", label: "50 pro Seite"},
    {value: "100", label: "100 pro Seite"},
];

/**
 * #8 Ladenhüter - die wichtigste Ansicht für die Lagerflächen-Frage:
 * Artikel mit Umsatz in der gesamten Historie, aber wenig/keinem Umsatz im
 * gewählten Betrachtungsfenster. Bekommt bewusst ein eigenes, hervorgehobenes
 * Kartenlayout statt eines gleichrangigen Diagramm-Kachel-Rasters.
 */
export default function SlowMoversTable({filters}) {
    const [windowMonths, setWindowMonths] = useState("24");
    const [perPage, setPerPage] = useState("50");
    const [page, setPage] = useState(1);

    const filterSignature = JSON.stringify(filters);
    useEffect(() => {
        // Filterwechsel oder ein anderes Fenster/eine andere Seitengröße
        // setzt die Seite zurück - sonst könnte eine bisher gültige Seite
        // plötzlich außerhalb des neuen Ergebnisses liegen.
        setPage(1);
    }, [filterSignature, windowMonths, perPage]);

    const params = useMemo(
        () => ({...filters, window: windowMonths, per_page: perPage, page}),
        [filters, windowMonths, perPage, page]
    );
    const {data, loading, error} = useStatisticsData("/statistics/slow-movers", params);
    const rows = data?.rows || [];
    const total = data?.total || 0;
    const lastPage = data?.last_page || 1;
    const recentNetKey = windowMonths === "12" ? "recent_12m_net_amount" : "recent_24m_net_amount";
    const recentQuantityKey = windowMonths === "12" ? "recent_12m_quantity" : "recent_24m_quantity";

    return (
        <Card className="shadow-sm" style={{borderColor: "var(--bs-warning)", borderWidth: 2}}>
            <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2 bg-warning-subtle">
                <span className="d-inline-flex align-items-center gap-1 fw-semibold">
                    Ladenhüter
                    <HelpTooltip placement="right" size={0.8}>
                        Artikel mit Umsatz in der gesamten Historie, aber wenig oder keinem Umsatz im gewählten
                        Betrachtungsfenster (12 oder 24 Monate, bezogen auf den jüngsten Beleg im Datenbestand).
                        Sortiert nach Monaten seit dem letzten Verkauf – oben stehen die Artikel, die am längsten
                        liegen. Das ist die zentrale Liste, um Lagerfläche zu erkennen, die ihr Umsatz nicht
                        rechtfertigt.
                    </HelpTooltip>
                </span>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <SelectList size="sm" value={windowMonths} options={WINDOW_OPTIONS} onChange={setWindowMonths} ariaLabel="Betrachtungsfenster" style={{minWidth: 260}} />
                    <SelectList size="sm" value={perPage} options={PER_PAGE_OPTIONS} onChange={setPerPage} ariaLabel="Zeilen pro Seite" style={{minWidth: 150}} />
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                {error && <Alert variant="danger" className="m-3 mb-0">{error}</Alert>}
                <div style={{overflowX: "auto"}}>
                    <Table hover size="sm" className="align-middle mb-0">
                        <thead>
                            <tr>
                                <th>Artikelnummer</th>
                                <th>Bezeichnung</th>
                                <th>Warengruppe</th>
                                <th>ABC</th>
                                <th className="text-end">Umsatz gesamt</th>
                                <th className="text-end">Umsatz im Fenster</th>
                                <th className="text-end">Menge im Fenster</th>
                                <th>Letzter Verkauf</th>
                                <th className="text-end">Monate ohne Verkauf</th>
                            </tr>
                        </thead>
                        <tbody style={{opacity: loading ? 0.6 : 1, transition: "opacity 120ms ease"}}>
                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="text-center text-muted py-4">
                                        Keine Ladenhüter im gewählten Zeitraum und Filter gefunden.
                                    </td>
                                </tr>
                            )}
                            {rows.map((row) => (
                                <tr key={row.article_number}>
                                    <td className="font-monospace">{row.article_number}</td>
                                    <td>{row.name || "–"}</td>
                                    <td>{row.article_group || "–"}</td>
                                    <td>
                                        {row.abc_class ? (
                                            <span className="badge" style={{backgroundColor: ABC_COLORS[row.abc_class] || OTHER_GROUP_COLOR}}>
                                                {row.abc_class}
                                            </span>
                                        ) : "–"}
                                    </td>
                                    <td className="text-end">{formatEuro(row.total_net_amount)}</td>
                                    <td className="text-end">{formatEuro(row[recentNetKey])}</td>
                                    <td className="text-end">{formatNumber(row[recentQuantityKey])}</td>
                                    <td>{formatDateDE(row.last_sale_date)}</td>
                                    <td className="text-end fw-semibold">{row.months_since_last_sale ?? "–"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>
            <Card.Footer className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <span className="small text-muted d-inline-flex align-items-center gap-2">
                    {loading && (
                        <Spinner animation="border" size="sm" role="status">
                            <span className="visually-hidden">Wird geladen…</span>
                        </Spinner>
                    )}
                    {total} Artikel gefunden, Seite {page} von {lastPage}
                </span>
                <div className="btn-group btn-group-sm">
                    <Button variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Zurück</Button>
                    <Button variant="outline-secondary" disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))}>Weiter</Button>
                </div>
            </Card.Footer>
        </Card>
    );
}
